

























































































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import {
  ActivityHistoryElement,
  ActivityHistoryStatus,
  ActivityHistoryType,
  DrivewealthAccountManagementType
} from "@/shared/types";
import Pagination from "@/shared/components/ui/Pagination.vue";
import { Location } from "vue-router";
import moment from "moment";
import { formatCurrency } from "@/shared/currency";
import Button from "@/shared/components/ui/Button.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { useDrivewealthStore } from "@/shared/store/drivewealth";

@Component({
  components: {
    Pagination,
    Button,
    GenericLoadingSpinner
  }
})
export default class Activity extends Vue {
  drivewealthStore = useDrivewealthStore();

  @Prop() pageNumber: number;

  @Prop() routeToDrivewealthActivity: (pageNumber: number) => Location;

  @Prop({ required: false })
  username: string;

  @Prop({ required: false })
  partnerId: number;

  @Prop() accountId: number;

  @Watch("$props", { deep: true })
  loadTable () {
    this.onPageNumberUpdated();
    if (this.accountId) {
      this.drivewealthStore.loadPendingActivityByAccount(
        {
          username: this.username, partnerId: this.partnerId,
          accountId: this.accountId, force: true
        });
    } else {
      this.drivewealthStore.loadPendingActivity({ username: this.username, partnerId: this.partnerId });
    }
    this.drivewealthStore.loadAdvisoryFunds({ username: this.username, partnerId: this.partnerId });
  }

  pendingVisible = true;

  get pendingActivityLoadedForAccount (): boolean {
    if (this.accountId) {
      return this.drivewealthStore.pendingActivityV2Loaded(this.accountId);
    }

    return this.drivewealthStore.pendingActivityLoaded;
  }

  get pendingActivityLoadingForAccount (): boolean {
    if (this.accountId) {
      return this.drivewealthStore.pendingActivityV2Loading(this.accountId);
    }

    return this.drivewealthStore.pendingActivityLoading;
  }

  get pendingActivityItemsForAccount (): ActivityHistoryElement[] {
    if (this.accountId) {
      const pendingActivity = this.drivewealthStore.pendingActivityById(this.accountId);

      return pendingActivity ? pendingActivity.pendingOrders : [];
    }

    return this.drivewealthStore.pendingActivity.pendingOrders;
  }

  get isLoading () {
    return this.pendingActivityLoadingForAccount || this.drivewealthStore.completedActivityLoading ||
      this.drivewealthStore.advisoryFundsLoading || this.drivewealthStore.drivewealthLoading;
  }

  get isManagedAccount () {
    return this.drivewealthStore.drivewealthLoaded && this.drivewealthStore.accountManagementType() ===
      DrivewealthAccountManagementType.RiaManaged;
  }

  get hasPendingActivity () {
    return this.pendingActivityLoadedForAccount &&
      this.pendingActivityItemsForAccount &&
      this.pendingActivityItemsForAccount.length > 0 &&
      !this.isManagedAccount;
  }

  get completedActivityText () {
    return this.isManagedAccount ? "Transaction History" : "Executed Transactions";
  }

  formattedShareAmount (shareAmount?: number) {
    return shareAmount === null ? "—" : shareAmount.toFixed(2);
  }

  created () {
    this.onPageNumberUpdated();
    if (this.accountId) {
      this.drivewealthStore.loadPendingActivityByAccount(
        {
          username: this.username, partnerId: this.partnerId,
          accountId: this.accountId, force: true
        });
    } else {
      this.drivewealthStore.loadPendingActivity({ username: this.username, partnerId: this.partnerId });
    }
    this.drivewealthStore.loadAdvisoryFunds({ username: this.username, partnerId: this.partnerId });
    this.drivewealthStore.loadDefaultAccount({ username: this.username, partnerId: this.partnerId });
  }

  @Watch("drivewealthStore.pendingActivityV2.items")
  @Watch("drivewealthStore.pendingActivityLoaded")
  onPendingActivityLoaded () {
    this.pendingVisible = this.pendingActivityItemsForAccount &&
      this.pendingActivityItemsForAccount.length > 0;
  }

  @Watch("pageNumber")
  onPageNumberUpdated () {
    this.drivewealthStore.setCompletedActivityPageNumber(this.pageNumber);
    if (this.accountId) {
      this.drivewealthStore.loadCompletedActivityByAccount(
        { username: this.username, partnerId: this.partnerId, accountId: this.accountId, force: true });
    } else {
      this.drivewealthStore.loadCompletedActivity({ username: this.username, partnerId: this.partnerId });
    }
  }

  formatDate (date?: moment.Moment) {
    if (!date) {
      return "";
    }
    return moment(date).format("MMM D, YYYY");
  }

  formatCurrency (currency, digits) {
    return formatCurrency(currency, digits);
  }

  onPageChanged (pageNumber: number) {
    this.routeToDrivewealthActivity(pageNumber);
  }

  hasOverflowText (a: ActivityHistoryElement) {
    return !!a.comment || !!a.drivewealthDocument || a.shareAmount > 0;
  }

  createDownloadLink (documentId: number): void {
    this.drivewealthStore.loadDocumentInTab({ documentId, username: this.username, partnerId: this.partnerId });
  }

  getAmount (p: ActivityHistoryElement) {
    if (p.status === ActivityHistoryStatus.Pending && p.type === ActivityHistoryType.Sell && p.dollarAmount === 0) {
      return "Sale Pending";
    }

    return this.formatCurrency(p.dollarAmount, 2);
  }

  getDescriptiveType (type: string, investmentFeeDescription: string): string {
    if (type === ActivityHistoryType.BillingFee) {
      return investmentFeeDescription || "Investment Platform Fee";
    } else if (type === ActivityHistoryType.TransactionFee) {
      return "Fee";
    } else if (type === ActivityHistoryType.DividendReinvestment) {
      return "Dividend Reinvestment";
    }
    return type;
  }

  showShareInfo (row: ActivityHistoryElement): boolean {
    return row.shareAmount && row.type !== ActivityHistoryType.DividendReinvestment;
  }

  mapToTableItems (elements: ActivityHistoryElement[]) {
    return elements.map((p, i) => ({
      trade_date: this.formatDate(p.tradeDate),
      settlement_date: this.formatDate(p.settledDate),
      instrument: { symbol: p.stockSymbol, companyName: p.companyName },
      transaction_type: this.getDescriptiveType(p.type, p.investmentFeeDescription),
      amount: this.getAmount(p),
      shareAmount: p.shareAmount === null ? "—" : p.shareAmount.toFixed(8),
      comment: p.comment,
      drivewealthDocument: p.drivewealthDocument,

      id: p.status + i,
      status: p.status,
      _showDetails: false,
      canExpand: this.hasOverflowText(p),
      showShareInfo: this.showShareInfo(p)
    }));
  }

  completedTableFields = [
    {
      key: "toggle",
      label: "",
      thClass: "toggle-col",
      tdClass: "pt-3", // I cannot figure out how to vertically-center this!
      // using a flex-box messes up the borders, and won't fill the space
    },
    {
      key: "trade_date",
      thClass: "text-left",
      label: "Date Initiated"
    },
    {
      key: "settlement_date",
      thClass: "text-left",
      label: "Executed Date"
    },
    {
      key: "instrument",
      thClass: "text-left",
      label: "Investment"
    },
    {
      key: "transaction_type",
      thClass: "text-left"
    },
    {
      key: "amount",
      label: "Amount",
      thClass: "text-right"
    },
  ];

  get completedTableItems () {
    return this.mapToTableItems(this.drivewealthStore.completedActivity);
  }

  pendingTableFields = [
    {
      key: "trade_date",
      label: "Date Initiated",
      thClass: "text-left"
    },
    {
      key: "instrument",
      thClass: "text-left",
      label: "Investment"
    },
    {
      key: "transaction_type",
      thClass: "text-left"
    },
    {
      key: "amount",
      label: "Amount",
      thClass: "text-right"
    },
  ];

  get pendingTableItems () {
    return this.mapToTableItems(this.pendingActivityItemsForAccount);
  }

  onRowClicked (item: any, index: number) {
    if (item.canExpand) {
      item._showDetails = !item._showDetails;
    }
  }
}
